import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { certificateService } from '@/services/certificate/certificate.service';
import type { CertificateVerificationResult } from '@/types/certificate.types';
import CertificatePdfPreview from '@/components/certificate/certificate-pdf-preview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Calendar, FileText, Shield } from 'lucide-react';

const CertificateVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const { verificationCode } = useParams<{ verificationCode: string }>();
  const navigate = useNavigate();
  const [code, setCode] = useState(verificationCode || '');
  const [result, setResult] = useState<CertificateVerificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAutoVerified, setHasAutoVerified] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);

  const handleVerify = async (codeToVerify?: string) => {
    const codeValue = codeToVerify || code;
    if (!codeValue.trim()) {
      setError(t('certificate.verification.pleaseEnterVerificationCode'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const verificationResult = await certificateService.verifyCertificate(codeValue);
      setResult(verificationResult);
      setVerifiedCode(codeValue.trim());
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || '';
      const statusCode = err.response?.status;
      
      // Check if it's an invalid UUID/verification code error (400 Bad Request)
      if (
        statusCode === 400 ||
        errorMessage.includes('Invalid certificate') ||
        errorMessage.includes('invalid input syntax for type uuid') ||
        errorMessage.includes('Invalid certificate verification code') ||
        errorMessage.toLowerCase().includes('invalid')
      ) {
        setError(t('certificate.verification.invalidCertificate'));
      } 
      // Check if certificate not found (404 Not Found)
      else if (
        statusCode === 404 ||
        errorMessage.includes('Certificate not found') ||
        errorMessage.includes('not found')
      ) {
        setError(t('certificate.verification.certificateNotFoundOrInvalid'));
      } 
      // Default error message
      else {
        setError(errorMessage || t('certificate.verification.certificateNotFoundOrInvalid'));
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // Automatic verification when verificationCode is in URL
  useEffect(() => {
    if (verificationCode && verificationCode.trim() && !hasAutoVerified) {
      setCode(verificationCode);
      setHasAutoVerified(true);
      handleVerify(verificationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, hasAutoVerified]);

  const getStatusIcon = (status: string, isValid: boolean) => {
    if (!isValid && status === 'expired') {
      return <XCircle className="h-5 w-5 text-red-600" />;
    }
    switch (status) {
      case 'valid':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'expired':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'forever':
        return <Shield className="h-5 w-5 text-green-600" />;
      default:
        return null;
    }
  };

  const getStatusBadgeClass = (status: string, isValid: boolean) => {
    if (isValid) {
      if (status === 'expired') {
        return 'bg-red-100 text-red-700';
      }
      return 'bg-green-100 text-green-700';
    }
    return 'bg-red-100 text-red-700';
  };

  const showManualInput = !verificationCode || !verificationCode.trim();

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {t('certificate.verification.title')}
          </h1>
          <p className="text-sm text-gray-600">
            {t('certificate.verification.description')}
          </p>
        </div>

        {showManualInput && (
          <Card className="mb-6 border-none shadow-none">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-gray-900">
                {t('certificate.verification.enterVerificationCode')}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600">
                {t('certificate.verification.verificationCodeDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder={t('certificate.verification.enterVerificationCodePlaceholder')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                  className="flex-1"
                />
                <Button onClick={() => handleVerify()} disabled={loading} className="main-dark-button">
                  {loading ? t('certificate.verification.verifying') : t('certificate.verification.verify')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && verificationCode && (
          <Card className="mb-6 border-none shadow-none bg-subprimary/50">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-600">{t('certificate.verification.verifying')}</p>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert className="mb-6 border-none shadow-none bg-red-50">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 font-medium">{error}</AlertDescription>
          </Alert>
        )}

        {result && result.certificate && (
          <div className="space-y-4">
            <Card className="border-none shadow-none bg-subprimary/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(result.certificate.status, result.valid)}
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">
                        {t('certificate.verification.certificateVerificationResult')}
                      </h2>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t(`certificate.verification.status.${result.certificate.status}`)}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getStatusBadgeClass(result.certificate.status, result.valid)}`}>
                    {t(`certificate.verification.status.${result.certificate.status}`)}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="flex items-start gap-3">
                    <FileText className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        {t('certificate.verification.certificateNumber')}
                      </div>
                      <div className="text-sm text-gray-900 font-semibold">
                        {result.certificate.certificateNumber}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        {t('certificate.verification.issueDate')}
                      </div>
                      <div className="text-sm text-gray-900 font-semibold">
                        {result.certificate.issueDate}
                      </div>
                    </div>
                  </div>
                </div>

                {result.certificate.reissuedAt && (
                  <p className="mt-4 text-sm text-gray-600">
                    {t('certificate.verification.reissuedOn', { date: result.certificate.reissuedAt })}
                  </p>
                )}

                <div className="mt-4">
                  {result.valid && (
                    <Alert className="bg-green-50 border-none shadow-none">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        {t('certificate.verification.thisCertificateIsValidAndAuthentic')}
                      </AlertDescription>
                    </Alert>
                  )}

                  {!result.valid && result.certificate.status === 'expired' && (
                    <Alert className="bg-red-50 border-none shadow-none">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800">
                        {t('certificate.verification.thisCertificateHasExpired')}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {result.certificate.hasDocument && verifiedCode && (
              <CertificatePdfPreview
                verificationCode={verifiedCode}
                certificateNumber={result.certificate.certificateNumber}
              />
            )}
          </div>
        )}

        <div className="mt-6">
          <Button variant="outline" onClick={() => navigate('/')} className="border-gray-300 text-gray-700 hover:bg-gray-50">
            {t('certificate.verification.backToHome')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerificationPage;

